import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, ChevronDown, Waves, Droplets, Check, Anchor } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function MapFilterBar({
  selectedState,
  setSelectedState,
  states,
  showRivers,
  setShowRivers,
  showLakes,
  setShowLakes,
  selectedPOITypes,
  setSelectedPOITypes,
  setShowPOIs,
  poiTypeConfig,
}) {
  return (
    <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
      {/* State Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="secondary" 
            size="sm" 
            className="h-10 px-4 bg-white hover:bg-gray-50 border border-gray-200 shadow-lg rounded-full"
            data-testid="state-filter"
          >
            <MapPin className="w-4 h-4 mr-2 text-gray-500" />
            {selectedState === "all" ? "All States" : selectedState}
            <ChevronDown className="w-4 h-4 ml-2 text-gray-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 max-h-[500px] overflow-y-auto">
          <DropdownMenuCheckboxItem
            checked={selectedState === "all"}
            onCheckedChange={() => setSelectedState("all")}
          >
            All States
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          {states.map(state => (
            <DropdownMenuCheckboxItem
              key={state}
              checked={selectedState === state}
              onCheckedChange={() => setSelectedState(state)}
            >
              {state}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    
      {/* Rivers Toggle */}
      <Button
        variant="secondary"
        size="sm"
        className={`h-10 px-4 bg-white hover:bg-gray-50 border border-gray-200 shadow-lg rounded-full ${
          showRivers ? 'ring-2 ring-cyan-500/50 bg-cyan-50' : ''
        }`}
        onClick={() => setShowRivers(!showRivers)}
        data-testid="rivers-toggle"
      >
        <Waves className={`w-4 h-4 mr-2 ${showRivers ? 'text-cyan-600' : 'text-gray-500'}`} />
        Rivers
        {showRivers && <Check className="w-4 h-4 ml-2 text-cyan-600" />}
      </Button>

      {/* Lakes Toggle */}
      <Button
        variant="secondary"
        size="sm"
        className={`h-10 px-4 bg-white hover:bg-gray-50 border border-gray-200 shadow-lg rounded-full ${
          showLakes ? 'ring-2 ring-cyan-500/50 bg-cyan-50' : ''
        }`}
        onClick={() => setShowLakes(!showLakes)}
        data-testid="lakes-toggle"
      >
        <Droplets className={`w-4 h-4 mr-2 ${showLakes ? 'text-cyan-600' : 'text-gray-500'}`} />
        Lakes
        {showLakes && <Check className="w-4 h-4 ml-2 text-cyan-600" />}
      </Button>

      {/* Points of Interest Filter */}
      {poiTypeConfig.types.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="secondary" 
              size="sm" 
              className={`h-10 px-4 bg-white hover:bg-gray-50 border border-gray-200 shadow-lg rounded-full ${
                selectedPOITypes.length > 0 ? 'ring-2 ring-blue-500/50' : ''
              }`}
              data-testid="poi-filter"
            >
              <Anchor className="w-4 h-4 mr-2 text-blue-500" />
              Points of Interest
              {selectedPOITypes.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs bg-blue-100 text-blue-700">
                  {selectedPOITypes.length}
                </Badge>
              )}
              <ChevronDown className="w-4 h-4 ml-2 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-[500px] overflow-y-auto">
            {Object.entries(poiTypeConfig.categories || {}).map(([categoryKey, categoryTypes]) => (
              <div key={categoryKey}>
                <DropdownMenuLabel className="text-xs uppercase tracking-wide text-gray-400">
                  {categoryKey.replace(/_/g, ' ')}
                </DropdownMenuLabel>
                {categoryTypes.map(poiType => (
                  <DropdownMenuCheckboxItem
                    key={poiType.type}
                    checked={selectedPOITypes.includes(poiType.type)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedPOITypes(prev => [...prev, poiType.type]);
                        setShowPOIs(true);
                      } else {
                        const next = selectedPOITypes.filter(t => t !== poiType.type);
                        setSelectedPOITypes(next);
                        if (next.length === 0) setShowPOIs(false);
                      }
                    }}
                  >
                    <span
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0 inline-block"
                      style={{ backgroundColor: poiType.color }}
                    />
                    {poiType.label}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
              </div>
            ))}
            <div className="flex gap-1 px-2 py-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs flex-1"
                onClick={() => {
                  setSelectedPOITypes(poiTypeConfig.types.map(t => t.type));
                  setShowPOIs(true);
                }}
              >
                All
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs flex-1"
                onClick={() => {
                  setSelectedPOITypes([]);
                  setShowPOIs(false);
                }}
              >
                None
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
